/**
 * Where a memory sits, so a lattice can be looked at.
 *
 * WHAT THIS IS FOR. The bay's claim is that one interpreter plus different
 * memory equals a different expert. Saying it is easy; a picture of it has to
 * show a real difference between two real things, or it is decoration.
 *
 * WHAT IS HONESTLY AVAILABLE, and this constrains the whole design. There is no
 * access to attention or activations through transformers.js, and inventing a
 * plausible-looking one would be exactly the ghost variable this project argues
 * against everywhere else. What IS measured, every turn, is which rows the
 * retrieval touched. So the picture is of the LATTICE, and the only thing that
 * moves is which of its facts were reached for.
 *
 * WHY POSITION MUST MEAN SOMETHING. Scattering points at random and lighting
 * them up would animate correctly while showing nothing: two lattices would
 * look alike, and a cluster of related facts firing together would look no
 * different from six unrelated ones. Projecting the stored embeddings puts
 * similar facts near each other, so "it reached into one region" and "it
 * reached all over" become visibly different events, which is the thing worth
 * seeing.
 *
 * PCA by power iteration rather than a library: two components off vectors that
 * are already in memory, no dependency, and deterministic, which a bench and a
 * screenshot both need.
 */

export type Projection = {
  /** Interleaved x,y in [-1, 1], one pair per row. */
  xy: Float32Array;
  count: number;
  /** Share of total variance the two axes carry. Low means the picture is flat. */
  explained: number;
};

/**
 * A fixed starting vector, because a random one makes the layout change on
 * every reload.
 *
 * Not a constant vector: an all-ones start is orthogonal to some real
 * eigenvectors and power iteration then converges to the wrong one. This is
 * cheap, spread, deterministic, and has no special relationship to any axis.
 */
function seedVector(dim: number, offset: number): Float32Array {
  const v = new Float32Array(dim);
  for (let i = 0; i < dim; i++) v[i] = Math.sin((i + 1) * 0.61803398875 + offset);
  return v;
}

function normalise(v: Float32Array): number {
  let n = 0;
  for (let i = 0; i < v.length; i++) n += v[i] * v[i];
  n = Math.sqrt(n);
  if (n < 1e-12) return 0;
  for (let i = 0; i < v.length; i++) v[i] /= n;
  return n;
}

/**
 * The leading eigenvector of the covariance, without forming the covariance.
 *
 * `X^T X v` computed as two passes over the rows, so the cost is O(rows * dim)
 * per iteration rather than O(dim^2) of storage. At 9,886 rows and 512 dims the
 * matrix would be a quarter of a million entries for no reason.
 */
function topComponent(
  rows: Float32Array,
  count: number,
  dim: number,
  mean: Float32Array,
  deflate: Float32Array[],
  offset: number,
  iterations = 40,
): Float32Array {
  const v = seedVector(dim, offset);
  normalise(v);
  const next = new Float32Array(dim);
  for (let it = 0; it < iterations; it++) {
    next.fill(0);
    for (let r = 0; r < count; r++) {
      const base = r * dim;
      let dot = 0;
      for (let d = 0; d < dim; d++) dot += (rows[base + d] - mean[d]) * v[d];
      for (let d = 0; d < dim; d++) next[d] += dot * (rows[base + d] - mean[d]);
    }
    // Remove any component already claimed by an earlier axis, so the second
    // axis is orthogonal to the first rather than a copy of it.
    for (const prev of deflate) {
      let dot = 0;
      for (let d = 0; d < dim; d++) dot += next[d] * prev[d];
      for (let d = 0; d < dim; d++) next[d] -= dot * prev[d];
    }
    if (normalise(next) === 0) break;
    v.set(next);
  }
  return v;
}

/**
 * Project row-major vectors to two dimensions.
 *
 * Returns coordinates in [-1, 1] with the aspect of the data preserved: both
 * axes are divided by the SAME scale, so a lattice that is genuinely elongated
 * looks elongated instead of being stretched to fill a square.
 */
export function project2d(rows: Float32Array, count: number, dim: number, iterations = 40): Projection {
  if (count === 0 || dim === 0) return { xy: new Float32Array(0), count: 0, explained: 0 };

  const mean = new Float32Array(dim);
  for (let r = 0; r < count; r++) {
    const base = r * dim;
    for (let d = 0; d < dim; d++) mean[d] += rows[base + d];
  }
  for (let d = 0; d < dim; d++) mean[d] /= count;

  const a1 = topComponent(rows, count, dim, mean, [], 0, iterations);
  const a2 = topComponent(rows, count, dim, mean, [a1], 1.7, iterations);

  const xy = new Float32Array(count * 2);
  let total = 0;
  let onPlane = 0;
  let rawSq = 0;
  for (let r = 0; r < count; r++) {
    const base = r * dim;
    let x = 0;
    let y = 0;
    let sq = 0;
    for (let d = 0; d < dim; d++) {
      const c = rows[base + d] - mean[d];
      x += c * a1[d];
      y += c * a2[d];
      sq += c * c;
      rawSq += rows[base + d] * rows[base + d];
    }
    xy[r * 2] = x;
    xy[r * 2 + 1] = y;
    total += sq;
    onPlane += x * x + y * y;
  }

  /**
   * A LATTICE WITH NO VARIANCE HAS NO LAYOUT, and saying so matters more than
   * it sounds.
   *
   * Caught by the degenerate test rather than by thinking about it. With every
   * row identical, the centred values are float noise around 1e-8 rather than
   * exactly zero, so `scale` becomes 1e-8 and the normalisation below AMPLIFIES
   * that noise into a full-frame scatter. The result animates correctly, fills
   * the panel, and is a picture of rounding error. `explained` came back at
   * 0.9999999980, which reads as "this projection captures everything".
   *
   * Relative rather than absolute, because the vectors here are unit-length and
   * a future pack might not be.
   */
  if (total <= 1e-9 * Math.max(rawSq, Number.MIN_VALUE)) {
    return { xy: new Float32Array(count * 2), count, explained: 0 };
  }

  // One scale for both axes. Dividing each by its own range would make every
  // lattice fill the frame identically and hide exactly the differences this
  // exists to show.
  let scale = 0;
  for (let i = 0; i < xy.length; i++) scale = Math.max(scale, Math.abs(xy[i]));
  if (scale > 0) for (let i = 0; i < xy.length; i++) xy[i] /= scale;

  return { xy, count, explained: onPlane / total };
}

/**
 * Dequantize a pack's int8 rows back to floats, row by row.
 *
 * Kept here rather than in `quant.ts` because that module owns the round trip
 * and this is a consumer of it. The projection needs every row at once, which
 * is the one access pattern `dequantizeRow` is awkward for.
 */
export function dequantizeAll(
  data: Int8Array,
  scales: Float32Array,
  count: number,
  dim: number,
): Float32Array {
  const out = new Float32Array(count * dim);
  for (let r = 0; r < count; r++) {
    const s = scales[r];
    for (let d = 0; d < dim; d++) out[r * dim + d] = data[r * dim + d] * s;
  }
  return out;
}
